import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import BannerImage from "../../sub-components/banner-image";
import {
  MapPin,
  Package,
  Wrench,
  ShieldAlert,
  FileSpreadsheet,
  TrendingUp,
  Activity,
  CheckCircle2,
  Mail,
  Phone,
  ArrowRight,
  Database,
  RefreshCw
} from "lucide-react";

const trackingConfig = {
  name: "Asset Tracking",
  tagline: "RFID & IoT Powered Asset Tracking System",
  hero: {
    title: "Track Every Asset.",
    titleHighlight: "Maximize Every Opportunity.",
    description: "Smart Tracking helps organizations gain real-time visibility, improve asset utilization and reduce losses with RFID-powered intelligence.",
    exploreLink: "#features",
    demoLink: "#demo",
    badges: [
      { text: "AI-Powered Tracking", color: "from-blue-500 to-indigo-500" },
      { text: "RFID Enabled", color: "from-cyan-500 to-blue-500" },
      { text: "Real-time Visibility", color: "from-purple-500 to-indigo-500" },
      { text: "Prevent Losses", color: "from-emerald-500 to-teal-500" }
    ],
    coverImage: "/assets/asset_tracking_cover_banner.png"
  },
  whatIs: {
    title: "What is Smart Tracking?",
    description: "Our Asset Tracking system is an enterprise-grade IoT platform that maps and monitors company inventory, heavy machineries, fleet shipments, and tools using rugged RFID tags and active GPS telemetry, giving organizations absolute operational visibility and audit-readiness."
  },
  features: {
    title: "Powerful Use Cases",
    subtitle: "Streamline inventory counts, track movements, and secure your fleet assets.",
    items: [
      {
        icon: MapPin,
        title: "Real-time Tracking",
        description: "Track assets in real-time across multiple geographic locations and job sites.",
        color: "from-blue-500/10 to-indigo-500/10"
      },
      {
        icon: Package,
        title: "Inventory Management",
        description: "Automate inventory counts, detect stock levels, and reduce counting discrepancies.",
        color: "from-cyan-500/10 to-blue-500/10"
      },
      {
        icon: Wrench,
        title: "Maintenance Management",
        description: "Schedule preventative maintenance logs, track usage hours, and extend asset lifespan.",
        color: "from-purple-500/10 to-indigo-500/10"
      },
      {
        icon: ShieldAlert,
        title: "Loss Prevention",
        description: "Get instant geofencing alerts, unauthorized movement alarms, and prevent tool shrinkage.",
        color: "from-red-500/10 to-orange-500/10"
      },
      {
        icon: FileSpreadsheet,
        title: "Compliance & Audit",
        description: "Maintain comprehensive digital audit trails and ensure regulatory compliance.",
        color: "from-emerald-500/10 to-teal-500/10"
      }
    ]
  },
  industries: [
    { name: "Construction", desc: "Track heavy equipment, tools, and contractor assets across job sites." },
    { name: "Healthcare", desc: "Monitor high-value medical assets, wheelchairs, and critical equipment." },
    { name: "Manufacturing", desc: "Optimize assembly line assets, parts inventory, and raw material workflows." },
    { name: "Logistics", desc: "Track fleet vehicles, shipping containers, and valuable cargo shipments." },
    { name: "Retail", desc: "Manage in-store assets, inventory levels, and reduce retail shrinkage." },
    { name: "Government", desc: "Ensure public asset accountability, audits, and optimize resource usage." }
  ],
  benefits: [
    "Centralized monitoring dashboard for all high-value assets",
    "Real-time geofencing boundaries and security sensors",
    "Automated counts with passive RFID tags",
    "Reduced asset retrieval and lookup times by 90%",
    "Extended asset life cycle through proactive maintenance alerts",
    "Custom compliance and utilization metrics reports",
    "Integrates with existing CRM, ERP, and databases",
    "Supports active GPS, BLE beacons, and passive RFID hardware"
  ],
  stats: [
    { value: "12,548", label: "Total Assets Tracked" },
    { value: "99.8%", label: "Accuracy Rate" },
    { value: "-18%", label: "Loss Reduction" },
    { value: "87%", label: "Asset Utilization" }
  ]
};

export default function AssetTracking() {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: ""
  });

  const baseUrl =
    process.env.NODE_ENV === "development"
      ? process.env.REACT_APP_BASE_URL_DEV
      : process.env.REACT_APP_BASE_URL_PROD;

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const data = new FormData();
    data.append("FullName", formData.fullName);
    data.append("Email", formData.email);
    data.append("Phone", formData.phone);
    data.append("Country", "UAE");
    data.append("Product", "Asset Tracking Demo Request");
    data.append("Message", "Requested a demo for RFID & IoT Asset Tracking System.");

    axios
      .post(`${baseUrl}/allDataInfo/contactformInfo.php`, data)
      .then(() => {
        setIsSubmitted(true);
        setFormData({ fullName: "", email: "", phone: "" });
      })
      .catch((error) => {
        console.error("Error submitting demo request:", error);
        alert("Something went wrong. Please try again or contact us directly at info@meerana.ae.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="bg-[#020617] text-slate-100 font-sans selection:bg-blue-500 selection:text-white overflow-hidden">
      <BannerImage />

      {/* Breadcrumb & Premium Header Section */}
      <div className="relative border-b border-slate-900 bg-slate-950/40 backdrop-blur-sm py-12 px-6 sm:px-12 lg:px-16 text-left">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff02_1px,transparent_1px),linear-gradient(to_bottom,#ffffff02_1px,transparent_1px)] bg-[size:30px_30px] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <nav className="text-[11px] sm:text-xs font-bold uppercase tracking-wider text-slate-500 mb-4">
            <Link to="/" className="hover:text-blue-405 transition-colors">Home</Link>
            <span className="mx-2 text-slate-700">/</span>
            <span className="text-slate-500">Solutions</span>
            <span className="mx-2 text-slate-700">/</span>
            <span className="text-blue-400">{trackingConfig.name}</span>
          </nav>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-[24px] lg:text-[28px] font-semibold text-white tracking-tight mb-2">
                {trackingConfig.name}
              </h1>
              <p className="text-slate-400 text-[14px] lg:text-[18px] font-normal max-w-2xl mt-2">
                {trackingConfig.tagline}
              </p>
            </div>
            <div className="flex-shrink-0">
              <span className="px-4 py-2 rounded-xl bg-blue-500/10 border border-blue-500/25 text-blue-400 text-xs font-bold uppercase tracking-widest">
                RFID & IoT Solution
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Blue Glow Accents */}
      <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute top-1/4 left-1/4 w-[550px] h-[550px] bg-indigo-500/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/3 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-[140px] pointer-events-none" />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 px-6 sm:px-12 lg:px-16 max-w-7xl mx-auto flex flex-col items-center text-center">
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {trackingConfig.hero.badges.map((badge, idx) => (
            <span
              key={idx}
              className={`px-3.5 py-1 text-xs font-semibold rounded-full bg-gradient-to-r ${badge.color} text-white shadow-md`}
            >
              {badge.text}
            </span>
          ))}
        </div>

        <h1 className="text-5xl sm:text-8xl font-black tracking-tight text-white mb-6 max-w-5xl leading-tight">
          {trackingConfig.hero.title}{" "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-cyan-400 to-indigo-455">
            {trackingConfig.hero.titleHighlight}
          </span>
        </h1>
        <p className="text-slate-400 text-lg sm:text-xl max-w-3xl mb-10 leading-relaxed">
          {trackingConfig.hero.description}
        </p>

        {/* Dynamic Cover Image Banner */}
        <div className="relative w-full max-w-4xl mx-auto rounded-2xl overflow-hidden border border-slate-800/80 shadow-2xl mb-12">
          <img
            src={trackingConfig.hero.coverImage}
            alt="Asset Tracking RFID Cover"
            className="w-full h-auto object-cover max-h-[400px] hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent pointer-events-none" />
        </div>

        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-16 z-10">
          <a
            href={trackingConfig.hero.exploreLink}
            className="px-8 py-4 rounded-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-650 hover:from-blue-500 hover:to-indigo-600 transition-all duration-300 shadow-[0_0_20px_rgba(59,130,246,0.35)] hover:shadow-[0_0_30px_rgba(59,130,246,0.55)] text-white"
          >
            Explore Use Cases
          </a>
        </div>

        {/* CSS-only Asset Tracking Console Simulator */}
        <div className="relative w-full max-w-4xl mx-auto rounded-2xl border border-slate-800/80 bg-slate-950/60 p-4 shadow-2xl backdrop-blur-md text-left font-sans mt-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-850 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-slate-500 text-xs font-bold ml-2">Smart Tracking Console v1.0</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
              <span className="text-blue-405 text-xs font-bold uppercase tracking-wider">Telemetry Connected</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

            {/* Sidebar Mock */}
            <div className="hidden lg:block lg:col-span-3 border-r border-slate-850 pr-4 space-y-2">
              <div className="px-3 py-2 bg-blue-600/10 border border-blue-500/20 text-blue-400 text-xs font-bold rounded-lg flex items-center gap-1.5">
                <Database className="w-3.5 h-3.5" /> Asset Registry
              </div>
              <div className="px-3 py-2 text-slate-400 text-xs font-semibold hover:bg-slate-900 rounded-lg transition-colors cursor-pointer flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> Geofences
              </div>
              <div className="px-3 py-2 text-slate-400 text-xs font-semibold hover:bg-slate-900 rounded-lg transition-colors cursor-pointer flex items-center gap-1.5">
                <Wrench className="w-3.5 h-3.5" /> Maintenance
              </div>
            </div>

            {/* Main Console */}
            <div className="lg:col-span-9 space-y-4">

              {/* Metric Row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 bg-slate-900/60 border border-slate-850 rounded-xl">
                  <p className="text-[10px] text-slate-500 uppercase font-bold">Total Assets</p>
                  <p className="text-lg font-black text-white">12,548</p>
                  <span className="text-[9px] text-emerald-400 font-bold">↑ 10.5%</span>
                </div>
                <div className="p-3 bg-slate-900/60 border border-slate-850 rounded-xl">
                  <p className="text-[10px] text-slate-500 uppercase font-bold">Active Assets</p>
                  <p className="text-lg font-black text-white">11,453</p>
                  <span className="text-[9px] text-emerald-400 font-bold">↑ 4.2%</span>
                </div>
                <div className="p-3 bg-slate-900/60 border border-slate-850 rounded-xl">
                  <p className="text-[10px] text-slate-500 uppercase font-bold">Alerts</p>
                  <p className="text-lg font-black text-white">32</p>
                  <span className="text-[9px] text-emerald-400 font-bold">↓ 18.2%</span>
                </div>
                <div className="p-3 bg-slate-900/60 border border-slate-850 rounded-xl">
                  <p className="text-[10px] text-slate-500 uppercase font-bold">Utilization</p>
                  <p className="text-lg font-black text-white">87%</p>
                  <span className="text-[9px] text-emerald-400 font-bold">↑ 5.1%</span>
                </div>
              </div>

              {/* Feed & Stream Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Simulated telemetry tracker map visual */}
                <div className="md:col-span-2 relative aspect-video bg-slate-950 rounded-xl overflow-hidden border border-slate-850 flex items-center justify-center">
                  
                  {/* Satellite Imagery Map Background */}
                  <img 
                    src="/assets/earth-night.jpg" 
                    alt="Satellite Tracking Map" 
                    className="absolute inset-0 w-full h-full object-cover opacity-30 select-none pointer-events-none"
                  />
                  <div className="absolute inset-0 bg-blue-950/15 pointer-events-none" />

                  {/* Glowing tracker dots */}
                  <div className="absolute top-10 left-16 flex flex-col items-center">
                    <span className="w-3 h-3 rounded-full bg-blue-500 animate-ping absolute" />
                    <span className="w-3 h-3 rounded-full bg-blue-500 relative" />
                    <span className="text-[8px] bg-slate-900/80 border border-slate-800 text-white px-1 mt-1 font-mono rounded">EXCAVATOR_123</span>
                  </div>

                  <div className="absolute bottom-16 right-20 flex flex-col items-center">
                    <span className="w-3 h-3 rounded-full bg-indigo-500 animate-ping absolute" />
                    <span className="w-3 h-3 rounded-full bg-indigo-500 relative" />
                    <span className="text-[8px] bg-slate-900/80 border border-slate-800 text-white px-1 mt-1 font-mono rounded">GENERATOR_RFID</span>
                  </div>

                  <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:15px_15px]" />
                  <div className="absolute bottom-3 left-3 text-slate-450 text-[10px] bg-slate-950/80 px-2 py-1 rounded font-mono border border-slate-850 flex items-center gap-1.5">
                    <MapPin className="w-3 h-3 text-blue-500" /> Geofence: Zone_B_SiteA
                  </div>
                </div>

                {/* Activity Feed */}
                <div className="p-4 bg-slate-950 border border-slate-850 rounded-xl space-y-3 flex flex-col justify-between text-xs">
                  <div>
                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block mb-2 border-b border-slate-900 pb-1.5">
                      Activity Logs
                    </span>
                    <div className="space-y-2.5">
                      <div className="border-b border-slate-900 pb-2">
                        <p className="font-bold text-slate-200">Asset Transferred</p>
                        <p className="text-[9px] text-slate-500">Excavator #123 moved to Site 03 • 5m ago</p>
                      </div>
                      <div className="border-b border-slate-900 pb-2">
                        <p className="font-bold text-slate-200">Maintenance Completed</p>
                        <p className="text-[9px] text-slate-500">Generator RFID logged complete • 12m ago</p>
                      </div>
                      <div>
                        <p className="font-bold text-slate-200">New Asset Registered</p>
                        <p className="text-[9px] text-slate-500">Excavator RFID added • 1h ago</p>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

            </div>

          </div>
        </div>
      </section>

      {/* What is Asset Tracking */}
      <section className="py-20 px-6 sm:px-12 lg:px-16 max-w-7xl mx-auto bg-slate-950/30 border-y border-slate-900">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <span className="text-blue-400 text-xs font-bold uppercase tracking-wider block mb-2">{trackingConfig.whatIs.title}</span>
            <h2 className="text-3xl sm:text-5xl font-black text-white mb-6">Complete Visibility. Optimized Lifespans.</h2>
            <p className="text-slate-350 text-base sm:text-lg leading-relaxed max-w-3xl">
              {trackingConfig.whatIs.description}
            </p>
          </div>
          <div className="lg:col-span-4 flex justify-center">
            <div className="w-24 h-24 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 shadow-xl shadow-blue-500/5">
              <MapPin className="w-12 h-12" />
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="features" className="py-20 px-6 sm:px-12 lg:px-16 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-blue-400 text-xs font-bold uppercase tracking-wider block mb-2">Capabilities</span>
          <h2 className="text-3xl sm:text-5xl font-black text-white mb-4">{trackingConfig.features.title}</h2>
          <p className="text-slate-400 max-w-xl mx-auto text-sm sm:text-base">{trackingConfig.features.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {trackingConfig.features.items.map((feat, idx) => {
            const IconComponent = feat.icon;
            return (
              <div
                key={idx}
                className="bg-slate-900/30 hover:bg-slate-900/60 border border-slate-900 hover:border-blue-500/30 rounded-2xl p-6 transition-all duration-300 group"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feat.color} flex items-center justify-center mb-6 border border-slate-800`}>
                  <IconComponent className="w-6 h-6 text-blue-400 group-hover:text-blue-355 transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-450 transition-colors">{feat.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{feat.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 px-6 sm:px-12 lg:px-16 max-w-7xl mx-auto bg-slate-950/40 border border-slate-900 rounded-3xl my-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

          {/* List panel */}
          <div className="lg:col-span-7">
            <span className="text-blue-400 text-xs font-bold uppercase tracking-wider block mb-2">Platform Value</span>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-8">Key Benefits of Smart Tracking</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {trackingConfig.benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300 text-sm">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Core Graphic Mockup */}
          <div className="lg:col-span-5 flex justify-center">
            <div className="relative w-72 h-72 sm:w-80 sm:h-80 rounded-full border border-blue-500/25 flex items-center justify-center bg-slate-900/20 shadow-[0_0_50px_rgba(59,130,246,0.05)]">
              <div className="w-48 h-48 rounded-full border border-blue-500/30 flex items-center justify-center bg-blue-950/20">
                <div className="w-24 h-24 rounded-full bg-gradient-to-r from-blue-600 to-indigo-650 flex items-center justify-center shadow-lg shadow-blue-500/30">
                  <span className="text-2xl font-black text-white">ST</span>
                </div>
              </div>

              {/* Orbiting metrics labels */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-slate-900 border border-slate-800 p-2.5 rounded-xl shadow-md text-xs font-semibold flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-blue-450" /> Geofences
              </div>
              <div className="absolute top-1/2 -right-6 -translate-y-1/2 bg-slate-900 border border-slate-800 p-2.5 rounded-xl shadow-md text-xs font-semibold flex items-center gap-1.5">
                <Package className="w-4 h-4 text-cyan-405" /> RFID Tagging
              </div>
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-slate-900 border border-slate-800 p-2.5 rounded-xl shadow-md text-xs font-semibold flex items-center gap-1.5">
                <RefreshCw className="w-4 h-4 text-purple-400" /> Active Sync
              </div>
              <div className="absolute top-1/2 -left-6 -translate-y-1/2 bg-slate-900 border border-slate-800 p-2.5 rounded-xl shadow-md text-xs font-semibold flex items-center gap-1.5">
                <Activity className="w-4 h-4 text-emerald-400" /> Telemetry
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Built For Every Industry */}
      <section className="py-20 px-6 sm:px-12 lg:px-16 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-blue-400 text-xs font-bold uppercase tracking-wider block mb-2">Sector Solutions</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Built for Every Industry</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {trackingConfig.industries.map((ind, idx) => (
            <div
              key={idx}
              className="bg-slate-950/60 border border-slate-900 p-6 rounded-2xl flex flex-col justify-between"
            >
              <div>
                <h3 className="text-lg font-bold text-white mb-2">{ind.name}</h3>
                <p className="text-slate-400 text-sm">{ind.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer Info */}
      {/* <footer className="py-12 border-t border-slate-900/60 bg-slate-950 px-6 sm:px-12 lg:px-16 text-center text-xs text-slate-500">
        <p>&copy; {new Date().getFullYear()} Meerana. All rights reserved.</p>
      </footer> */}

    </div>
  );
}
